import {Edge} from 'reactflow';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  makeDataFlowEdgeHandle,
  HANDLE_TYPE_NODE,
  HANDLE_TYPE_FIELD,
  HANDLE_DIRECTION_SOURCE,
  HANDLE_DIRECTION_TARGET,
  FIELD_TYPE_INPUT,
  FIELD_TYPE_OUTPUT,
  HandleDirection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {getAllNodeDetailsSidepanelTabsForTaskNodeData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/getAllNodeDetailsSidepanelTabsForTaskNode';
import {TaskSchemaNodeSectionType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNodeData,
  TaskSchemaNodeRowData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';

import {HandlesContextValue} from '../HandlesContext';
import {PlatformFlowchartFeatures} from '../PlatformFeaturesContext';

export type TaskSchemaNodeRowRendererProps = TaskSchemaNodeRowData & {
  isHovered: boolean;
};

export interface ShouldHideHandleParams {
  rowData: TaskSchemaNodeRowRendererProps;
  handleDirection: HandleDirection;
  connectionDragState: HandlesContextValue['connectionDragState'];
  platformFlowchartFeatures: PlatformFlowchartFeatures | undefined;
  currentNodeId: string | null;
  downstreamNodeIdsFromSource: Array<string> | null;
  downstreamNodeIdsFromCurrentNode: Array<string> | null;
}

/**
 * Determines whether a handle should be hidden based on node type, handle direction, and drag state.
 *
 * @package - exported for testing only
 * @returns true if the handle should be hidden, false if it should be visible
 */
export function shouldHideHandle({
  rowData,
  handleDirection,
  connectionDragState,
  platformFlowchartFeatures,
  currentNodeId,
  downstreamNodeIdsFromSource,
  downstreamNodeIdsFromCurrentNode,
}: ShouldHideHandleParams): boolean {
  const isCurrentNodeFlowchartInputNode =
    rowData.sectionType === TaskSchemaNodeSectionType.INPUTS && rowData.isFlowchartTask;
  const isCurrentNodeFlowchartOutputNode =
    rowData.sectionType === TaskSchemaNodeSectionType.OUTPUTS && rowData.isFlowchartTask;

  if (rowData.sectionType === TaskSchemaNodeSectionType.NOT_MAPPABLE) {
    return true;
  }
  if (isCurrentNodeFlowchartInputNode && handleDirection === HANDLE_DIRECTION_TARGET) {
    return true;
  } else if (isCurrentNodeFlowchartOutputNode && handleDirection === HANDLE_DIRECTION_SOURCE) {
    return true;
  }

  const isInputSectionType =
    rowData.sectionType === TaskSchemaNodeSectionType.INPUTS ||
    rowData.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START;
  if (
    platformFlowchartFeatures?.get(ProcessFlowchartPlatformFeatureType.ALLOW_NODE_INPUTS_AS_EDGE_SOURCE) !==
      true &&
    isInputSectionType &&
    !isCurrentNodeFlowchartInputNode &&
    handleDirection === HANDLE_DIRECTION_SOURCE
  ) {
    return true;
  }

  if (connectionDragState?.isDragging && connectionDragState.sourceHandleInfo) {
    const sourceInfo = connectionDragState.sourceHandleInfo;

    // Always hide field handles if dragging from a node handle (control flow handle)
    if (sourceInfo.handleCategory === HANDLE_TYPE_NODE) {
      return true;
    }

    if (sourceInfo.handleCategory === HANDLE_TYPE_FIELD) {
      if (sourceInfo.direction === handleDirection) {
        return true;
      }

      const isDownstreamOfSource =
        currentNodeId != null &&
        downstreamNodeIdsFromSource != null &&
        downstreamNodeIdsFromSource.includes(currentNodeId);
      if (
        platformFlowchartFeatures?.get(ProcessFlowchartPlatformFeatureType.ALLOW_LOOPS) !== true &&
        !isDownstreamOfSource &&
        handleDirection === HANDLE_DIRECTION_TARGET
      ) {
        return true;
      }

      const currentFieldType =
        rowData.sectionType === TaskSchemaNodeSectionType.INPUTS ? FIELD_TYPE_INPUT : FIELD_TYPE_OUTPUT;

      if (currentFieldType === FIELD_TYPE_OUTPUT) {
        // Allow connections to output fields only when target is an output node
        return !isCurrentNodeFlowchartOutputNode;
      }

      return false;
    }

    return false;
  }

  // We aren't dragging, we should not show target handles
  if (handleDirection === HANDLE_DIRECTION_TARGET) {
    return true;
  }

  // Hide source handles when ALLOW_LOOPS is false and there are no downstream nodes
  if (
    platformFlowchartFeatures?.get(ProcessFlowchartPlatformFeatureType.ALLOW_LOOPS) !== true &&
    handleDirection === HANDLE_DIRECTION_SOURCE &&
    downstreamNodeIdsFromCurrentNode != null &&
    downstreamNodeIdsFromCurrentNode.length === 0
  ) {
    return true;
  }

  return false;
}

/**
 * Checks if the row has handles connected to it, returns booleans for the source and target handles.
 */
export function doesRowHaveHandles(
  nodeId: string | null,
  clientId: string,
  sectionType: TaskSchemaNodeSectionType,
  edges: Array<Edge>
): {isSourceConnected: boolean; isTargetConnected: boolean} {
  if (nodeId == null) {
    return {isSourceConnected: false, isTargetConnected: false};
  }

  const sourceRowHandleId = makeDataFlowEdgeHandle({
    nodeId,
    fieldClientId: clientId,
    sectionType,
    handleType: 'source',
  });
  const targetRowHandleId = makeDataFlowEdgeHandle({
    nodeId,
    fieldClientId: clientId,
    sectionType,
    handleType: 'target',
  });

  const isSourceConnected = edges.some((edge) => edge.sourceHandle === sourceRowHandleId);
  const isTargetConnected = edges.some((edge) => edge.targetHandle === targetRowHandleId);

  return {isSourceConnected, isTargetConnected};
}

/**
 * Check if any tab in the node's sidepanel content supports field selection.
 * Fields should only be clickable if there's a tab that can display field-specific content.
 * This considers both adapter-defined tabs and platform feature tabs.
 */
export function hasFieldSelectableTab(
  nodeData: TaskSchemaNodeData | null,
  platformFlowchartFeatures: PlatformFlowchartFeatures | undefined
): boolean {
  if (nodeData == null) {
    return false;
  }
  const allTabs = getAllNodeDetailsSidepanelTabsForTaskNodeData(nodeData, platformFlowchartFeatures);
  return allTabs.some((tab) => tab.openOnFieldSelect === true);
}
